import React from 'react'

import { useTranslation } from 'react-i18next'

import {
  CardRoot,
  CloseButton,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPositioner,
  DialogRoot,
  HStack,
  PrimaryButton,
  Stack,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'

import { useAllContractCsvExport } from '~/pages/ContractList/hooks/useAllContractCsvExport'
import { ContractListFilterState } from '~/pages/ContractList/hooks/useContractListFilter'
import generateDeliveryPeriodText from '~/pages/ContractList/utils/deliveryPeriodFormatter'

import type { ContractDealReportModel } from '../../models/contractDealReport'

type AllContractsExportModalProps = {
  open: boolean
  onClose: () => void
  data?: ContractDealReportModel[]
  filterState: ContractListFilterState
}

export const AllContractsExportModal: React.FC<
  AllContractsExportModalProps
> = ({ open, onClose, data, filterState }) => {
  const { t } = useTranslation(['page', 'common'])
  const { exportCsv } = useAllContractCsvExport(data, filterState)
  const handleExport = (): void => {
    exportCsv()
    onClose()
  }

  return (
    <DialogRoot
      open={open}
      onOpenChange={(e) => {
        if (!e.open) {
          onClose()
        }
      }}
      size="lg"
      placement="center"
    >
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent>
          <DialogHeader>
            {t('page:contract_list.table.export_modal.title', {
              value: t('page:contract_list.table.export_menu.all_contracts'),
            })}
          </DialogHeader>
          <DialogCloseTrigger asChild>
            <CloseButton size="sm" />
          </DialogCloseTrigger>
          <DialogBody>
            <Stack gap="4">
              <Text>
                {t(
                  'page:contract_list.table.export_modal.all_contracts_description',
                  {
                    count: data?.length ?? 0,
                  },
                )}
              </Text>
              <Stack gap="2">
                <Text textStyle="headerMd">
                  {t('page:contract_list.filter.filter_conditions')}
                </Text>
                <CardRoot
                  borderColor="transparent"
                  backgroundColor="Surface.secondary"
                  padding="4"
                  display="flex"
                  flexDirection="column"
                  gap="2"
                >
                  <HStack
                    gap="4"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                  >
                    <Text fontWeight="bold">
                      {t('page:contract_list.filter.delivery_period')}
                    </Text>
                    <Text>{generateDeliveryPeriodText(filterState)}</Text>
                  </HStack>
                  <HStack
                    gap="4"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                  >
                    <Text fontWeight="bold">
                      {t('page:contract_list.filter.display_conditions_label')}
                    </Text>
                    <Text>
                      {filterState.excludeSettled
                        ? t('page:contract_list.filter.exclude_settled')
                        : t('page:contract_list.filter.include_settled')}
                    </Text>
                  </HStack>
                </CardRoot>
              </Stack>
            </Stack>
          </DialogBody>
          <DialogFooter>
            <HStack gap="4">
              <TertiaryButton onClick={onClose}>
                {t('common:action.negative.cancel')}
              </TertiaryButton>
              <PrimaryButton
                onClick={() => {
                  handleExport()
                }}
              >
                {t('common:action.positive.start_download')}
              </PrimaryButton>
            </HStack>
          </DialogFooter>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  )
}
